import { documentStepModifier } from '../shared/document-step-keys';
import { hostHas, type HostFeature } from '../shared/host-capabilities';
import { shortcutGroups, type ShortcutGroup, type ShortcutRow } from '../renderer/shortcut-list';
import { installPaigeHost } from './paige-host';

/**
 * Opens the F1 sheet in the workbench from outside its keyboard: the status bar's keyboard button and Paige's
 * Help sheet (gap list C8; Dustin, 2026-10-20). ShortcutSheet.tsx listens, through one PAIGE seam.
 */
export const OPEN_SHORTCUTS_EVENT = 'paige-open-shortcuts';

/** Asks the workbench's F1 sheet to open. */
export function openShortcutSheet(): void {
  window.dispatchEvent(new Event(OPEN_SHORTCUTS_EVENT));
}

/**
 * The F1 sheet in a host that is not the desktop: a key is listed only where it works.
 *
 * CapturePoint's list (renderer/shortcut-list.ts) is every key of the desktop application. Some
 * belong to a screen or a window a host does not have; listing them would promise a key that does
 * nothing, or one the browser answers itself (Ctrl+W closes the tab).
 */
const NEEDS: Readonly<Record<string, HostFeature>> = {
  // The desktop's own settings screen.
  'Ctrl+,': 'workbench-settings',
  // A job's card in the library of scan jobs (F2 also renames in the job switcher: hostShortcutRow).
  Delete: 'job-library',
  // The application window's keys; a browser's window and tab are the browser's.
  F11: 'window-keys',
  'Ctrl+M': 'window-keys',
  'Ctrl+W': 'window-keys',
};

/** Whether this host has the screen or window the row's keys belong to. */
export function hostHasShortcut(row: Pick<ShortcutRow, 'keys'>): boolean {
  const feature = NEEDS[row.keys];
  return feature === undefined || hostHas(feature);
}

/**
 * The row as this host's people read it. Without the job library F2 still renames the job from the job
 * switcher (renderer/JobSelector.tsx), so the row stays and says only where it works.
 */
export function hostShortcutRow<Row extends Pick<ShortcutRow, 'keys' | 'action' | 'note'>>(
  row: Row,
): Row {
  const keys = hostShortcutKeys(row.keys);
  if (row.keys !== 'F2' || hostHas('job-library')) return { ...row, keys };
  return { ...row, keys, action: 'Rename (in the job switcher)', note: undefined };
}

/**
 * The F1 sheet's groups as Paige lists them, for a screen outside the workbench (the dashboard's Help, and F1
 * there): the review screen's keys, filtered and worded for the browser exactly as the workbench's own sheet.
 */
export function paigeShortcutGroups(): ShortcutGroup[] {
  installPaigeHost();
  return shortcutGroups('main')
    .map((group) => ({
      title: group.title,
      rows: group.rows.filter(hostHasShortcut).map(hostShortcutRow),
    }))
    .filter((group) => group.rows.length > 0);
}

/** The keys as this host's people press them: a browser keeps Ctrl+PgDn for its tabs, so its step is Alt. */
export function hostShortcutKeys(keys: string): string {
  return keys.replaceAll('Ctrl+Pg', `${documentStepModifier()}+Pg`);
}
